import {makeApiCall} from '../core';
import type {
    CreateOneOffSubscriptionRequest,
    CreateOneOffSubscriptionResponse,
    ItemEnvelope,
} from '@ruta/shared';

export type { CreateOneOffSubscriptionRequest, CreateOneOffSubscriptionResponse };

// =====================
// Subscription Statistics API
// =====================

export interface SubscriptionStats {
  total: number;
  activeSubscriptions: number;
  inactiveSubscriptions: number;
  pendingCount: number;  // Services with rates configured but not visible to customers
  visibleRates: number;
  invisibleRates: number;
  activeVisible: number;
  activeInvisible: number;
  inactiveVisible: number;
  inactiveInvisible: number;
  needsScheduling: number;
  needsCrew: number;
  serviceTemplateBreakdown?: Record<string, {
    name: string;  // Template name for display
    active: number;
    inactive: number;
    total: number;
  }>;
}

export const getSubscriptionStats = async (serviceTemplateId?: string): Promise<SubscriptionStats> => {
  const endpoint = serviceTemplateId
    ? `/subscriptions/stats?serviceTemplateId=${encodeURIComponent(serviceTemplateId)}`
    : '/subscriptions/stats';
  const res = await makeApiCall<{ data: SubscriptionStats }>('GET', endpoint);
  return res.data;
};

export interface UpcomingDeactivation {
  subscriptionId: string;
  customerId: string;
  serviceTemplateId: string;
  customerName: string;
  serviceName: string;
  deactivationDate: string;
  reason?: string;
}

export const getUpcomingDeactivations = async (
  withinDays = 60,
): Promise<UpcomingDeactivation[]> => {
  const res = await makeApiCall<{ data: UpcomingDeactivation[] }>(
    'GET',
    `/subscriptions/upcoming-deactivations?withinDays=${withinDays}`,
  );
  return res.data;
};

// =====================
// Subscription State Management API
// =====================

export interface SubscriptionStateUpdate {
  customerId: string;
  serviceTemplateId: string;
  subscriptionStatus?: 'active' | 'inactive';
  rateVisibility?: 'visible' | 'invisible';
  pricingLocked?: boolean;
  reviewed?: boolean;
  rate?: number; // Update subscription rate (propagates to future visits)
  budgetedHours?: number; // Update budgeted hours
  /** Per-option package totals + per-visit rates (bulk package pricing). */
  packageOptionPricing?: import('@ruta/shared').PackageOptionPricing[];
  pricePerAdditionalVisit?: number;
  reason?: string;
  // Structured deactivation reasons (RUT-1368) — selected DEACTIVATION_REASON picklist
  // labels, with "Custom" replaced by the typed free text. Stored on deactivation.
  reasons?: string[];
  date?: string;
  /** YYYY-MM-DD scheduled stop. Distinct from `date` (change-history timestamp). */
  deactivationDate?: string;
  deactivationCrewNote?: string;
  // Contact-level billing
  billingContactId?: string | null; // Which contact to bill (null to clear)
  paymentMethodId?: string | null; // Specific payment method to use (null to clear)
  // Cancellation: when subscriptionStatus flips to 'inactive', notifyCustomer=true
  // sends SERVICE_CANCELLED with the optional admin-authored customerMessage.
  notifyCustomer?: boolean;
  customerMessage?: string;
  // Rate-request snooze (RUT-2698). YYYY-MM-DD (future) hides the pending
  // request from the rate-request metric until that date; null unsnoozes.
  snoozeUntil?: string | null;
  // Flag the service for a later rate review (true sets, false clears).
  rateReviewFlagged?: boolean;
  // The finished visit that prompted the flag, recorded for attribution.
  rateReviewVisitId?: string;
}

export interface BulkSubscriptionStateRequest {
  subscriptionUpdates: SubscriptionStateUpdate[];
}

export interface BulkSubscriptionStateResponse {
  message: string;
  totalRequested: number;
  totalUpdated: number;
  totalSkipped: number;
  totalErrors: number;
  updatedSubscriptions: Array<{
    subscriptionId: string;
    customerId: string;
    serviceTemplateId: string;
    previousState: string;
    newState: string;
  }>;
  errors?: Array<{
    customerId: string;
    serviceTemplateId: string;
    error: string;
    code?: string;
  }>;
}

export const patchSubscriptionStates = async (
  requestData: BulkSubscriptionStateRequest,
): Promise<BulkSubscriptionStateResponse> => {
  const res = await makeApiCall<{ data: BulkSubscriptionStateResponse }>('PATCH', '/subscriptions', requestData);
  return res.data;
};

// Bulk visit creation from subscriptions
export interface BulkVisitFromSubscriptionRequest {
  customerId: string;
  serviceTemplateId: string;
  notes?: string;
  notes_es?: string;
  order?: number;
  // Optional pre-fetched subscription data for efficiency
  subscription?: any;
}

export interface BulkVisitsFromSubscriptionsResponse {
  visits: Array<{
    id: string;
    customerId: string;
    customerName: string;
    serviceTemplateId: string;
    serviceName: string;
    assignedCrewId?: string;
    budgetedHours?: number;
  }>;
  cancelledCount?: number;
  errors?: Array<{
    customerId: string;
    serviceTemplateId: string;
    error: string;
    code?: string;
  }>;
}

export const createVisitsFromSubscriptions = async (
  visits: BulkVisitFromSubscriptionRequest[],
): Promise<BulkVisitsFromSubscriptionsResponse> => {
  const res = await makeApiCall<{ data: BulkVisitsFromSubscriptionsResponse }>(
    'POST',
    '/subscriptions/visits',
    { visits },
  );
  return res.data;
};

/**
 * Admin-create a one-off subscription for a customer. Posts to the
 * customer-rooted subscription route — same shape as activating a
 * templated sub, just with the inline `oneOffTemplate` block instead of
 * a `serviceTemplateId`.
 */
export const createOneOffSubscription = async (
  customerId: string,
  data: CreateOneOffSubscriptionRequest,
): Promise<CreateOneOffSubscriptionResponse> => {
  const res = await makeApiCall<ItemEnvelope<CreateOneOffSubscriptionResponse>>(
    'POST',
    `/customers/${customerId}/subscriptions/one-off`,
    data,
  );
  return res.data;
};

